import { supabase } from '../lib/supabase';

export const lessonsService = {
  /**
   * Fetch all lessons for a module
   */
  async getLessonsByModule(moduleId) {
    try {
      const { data, error } = await supabase
        .from('lessons')
        .select('*')
        .eq('module_id', moduleId)
        .order('lesson_number', { ascending: true });

      if (error) throw error;
      return data || [];
    } catch (err) {
      console.error('Error fetching lessons by module:', err);
      return [];
    }
  },

  /**
   * Fetch a single lesson by module and lesson number
   */
  async getLesson(moduleId, lessonNumber) {
    try {
      const { data, error } = await supabase
        .from('lessons')
        .select('*')
        .eq('module_id', moduleId)
        .eq('lesson_number', lessonNumber)
        .single();

      if (error) throw error;
      return data || null;
    } catch (err) {
      console.error('Error fetching lesson:', err);
      return null;
    }
  },

  /**
   * Create a new lesson
   */
  async createLesson(lessonData) {
    try {
      const { data, error } = await supabase
        .from('lessons')
        .insert([lessonData])
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (err) {
      console.error('Error creating lesson:', err);
      throw err;
    }
  },

  /**
   * Update a lesson
   */
  async updateLesson(lessonId, updates) {
    try {
      const { data, error } = await supabase
        .from('lessons')
        .update(updates)
        .eq('id', lessonId)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (err) {
      console.error('Error updating lesson:', err);
      throw err;
    }
  },

  /**
   * Upsert lesson (insert or update based on module_id + lesson_number)
   */
  async upsertLesson(lessonData) {
    try {
      const { data, error } = await supabase
        .from('lessons')
        .upsert([lessonData], { onConflict: 'module_id,lesson_number' })
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (err) {
      console.error('Error upserting lesson:', err);
      throw err;
    }
  },

  /**
   * Delete a lesson
   */
  async deleteLesson(lessonId) {
    try {
      const { error } = await supabase
        .from('lessons')
        .delete()
        .eq('id', lessonId);

      if (error) throw error;
      return true;
    } catch (err) {
      console.error('Error deleting lesson:', err);
      throw err;
    }
  },

  /**
   * Fetch lessons with pagination
   */
  async getLessonsPageinated(options = {}) {
    const { 
      moduleId = null,
      limit = 100, 
      offset = 0,
      order = { column: 'lesson_number', ascending: true }
    } = options;

    try {
      let query = supabase.from('lessons').select('*', { count: 'exact' });

      if (moduleId) {
        query = query.eq('module_id', moduleId);
      }

      query = query.order(order.column, { ascending: order.ascending })
        .range(offset, offset + limit - 1);

      const { data, error, count } = await query;

      if (error) throw error;

      return {
        lessons: data || [],
        count: count || 0,
        hasMore: (offset + limit) < (count || 0)
      };
    } catch (err) {
      console.error('Error fetching lessons paginated:', err);
      return { lessons: [], count: 0, hasMore: false };
    }
  },

  /**
   * Bulk insert or update lessons (for CSV import)
   */
  async bulkUpsertLessons(lessons) {
    try {
      const { data, error } = await supabase
        .from('lessons')
        .upsert(lessons, { onConflict: 'module_id,lesson_number' })
        .select();

      if (error) throw error;
      return data || [];
    } catch (err) {
      console.error('Error bulk upserting lessons:', err);
      throw err;
    }
  }
};
